/* This hook implements the "Rana droplets". It returns a ref -- set the ref on a HTML Div element,
   and droplets will magically appear in it. */

import { useEffect, useRef } from "react";

import { DEFAULT_COLORS } from "@nens/ranalib";
import { useAnimationFrame, useMousePosition } from "@nens/react/hooks";

function resolveColorInput(input: string): string {
  if (/^[a-z_]+\.[a-z_0-9]+$/i.test(input)) {
    const [category, key] = input.split(".") as [keyof typeof DEFAULT_COLORS, string];
    const section = DEFAULT_COLORS[category] as Record<string, string>;
    return section[key] ?? input;
  }
  return input;
}

interface RanaDropletsConfig {
  offsetElementId?: string;
  offsetX?: number;
  offsetY?: number;
  squareSize?: number;
  backgroundColor?: string;
  dropletColor?: string;
  alignX?: "left" | "right";
  alignY?: "top" | "bottom";
  radiusFunction?: (x: number, y: number) => number;
}

type Position = { x: number; y: number };

export function useRanaDroplets(config: RanaDropletsConfig) {
  const theDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Find the div we're connected to.
    const theDiv = theDivRef.current;
    if (theDiv === null) return;

    const mainDivPosition: Position = { x: theDiv.clientLeft, y: theDiv.clientTop };

    // Let's make a equally sized canvas
    const theCanvas = document.createElement("canvas");
    theCanvas.width = theDiv.clientWidth;
    theCanvas.height = theDiv.clientHeight;

    // Draw the droplets
    if (theCanvas !== null) {
      const context = theCanvas.getContext("2d");
      if (context !== null) {
        drawDroplets(
          context,
          theDiv,
          theCanvas.width,
          theCanvas.height,
          mainDivPosition,
          null,
          config
        );
      }
    }

    // And set it as the div's background image. That's all!
    theDiv.style.background = resolveColorInput(config.backgroundColor ?? "brand.glacier");
    theDiv.style.backgroundImage = `url(${theCanvas.toDataURL()})`;
  }, [config]);

  return theDivRef;
}

export function useAnimatedRanaDroplets(config: RanaDropletsConfig) {
  const theDivRef = useRef<HTMLDivElement>(null);
  const mouseRef = useMousePosition();

  useAnimationFrame(() => {
    // Find the div we're connected to.
    const theDiv = theDivRef.current;
    if (theDiv === null) return;

    const mainDivPosition: Position = { x: theDiv.clientLeft, y: theDiv.clientTop };

    // Let's make a equally sized canvas
    const theCanvas = document.createElement("canvas");
    theCanvas.width = theDiv.clientWidth;
    theCanvas.height = theDiv.clientHeight;

    // Draw the droplets
    if (theCanvas !== null) {
      const context = theCanvas.getContext("2d");
      if (context !== null) {
        drawDroplets(
          context,
          theDiv,
          theCanvas.width,
          theCanvas.height,
          mainDivPosition,
          mouseRef.current,
          config
        );
      }
    }

    // And set it as the div's background image. That's all!
    theDiv.style.background = resolveColorInput(config.backgroundColor ?? "brand.glacier");
    theDiv.style.backgroundImage = `url(${theCanvas.toDataURL()})`;
  }, 2 /* frames per second */);

  return theDivRef;
}

function drawDroplets(
  theContext: CanvasRenderingContext2D,
  theDiv: HTMLDivElement,
  width: number,
  height: number,
  mainDivPosition: Position,
  mousePosition: Position | null,
  config: RanaDropletsConfig
) {
  const squareSize = config.squareSize ?? 40;
  let offsetX = config.offsetX ?? 0;
  let offsetY = config.offsetY ?? 0;

  if (config.offsetElementId) {
    const element = document.getElementById(config.offsetElementId);

    if (element) {
      const rect = element.getBoundingClientRect();
      offsetX += rect.x - mainDivPosition.x;
      offsetY += rect.y - mainDivPosition.y;

      if (config.alignX === "right") {
        offsetX += rect.width;
      }
      if (config.alignY === "bottom") {
        offsetY += rect.height;
      }
    }
  } else if (config.alignX || config.alignY) {
    const rect = theDiv.getBoundingClientRect();
    if (config.alignX === "right") {
      offsetX += rect.width;
    }
    if (config.alignY === "bottom") {
      offsetY += rect.height;
    }
  }

  let mouseSquare: Position | null = null;
  if (mousePosition !== null) {
    const mouseOnCanvas: Position = {
      x: mousePosition.x - offsetX,
      y: mousePosition.y - offsetY,
    };
    mouseSquare = {
      x: Math.round(mouseOnCanvas.x / squareSize),
      y: Math.round(mouseOnCanvas.y / squareSize),
    };
  }

  const columns = Math.ceil(width / squareSize);
  const startSquareX = -Math.ceil(offsetX / squareSize);
  const rows = Math.ceil(height / squareSize);
  const startSquareY = -Math.ceil(offsetY / squareSize);

  for (let column = 0; column < columns; column++) {
    const squareX = column + startSquareX;
    for (let row = 0; row < rows; row++) {
      const squareY = row + startSquareY;
      const radiusFunction = config.radiusFunction ?? (() => 1);
      let radius = radiusFunction(squareX, squareY);
      if (mouseSquare && mouseSquare.x === squareX && mouseSquare.y === squareY) {
        radius = 4;
      }

      drawCircle(
        theContext,
        offsetX + squareX * squareSize,
        offsetY + squareY * squareSize,
        radius,
        resolveColorInput(config.dropletColor ?? "brand.glacier")
      );
    }
  }
}

function drawCircle(
  theContext: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string
) {
  theContext.beginPath();
  theContext.arc(x, y, radius * 5, 0, 2 * Math.PI);
  theContext.fillStyle = color;
  theContext.fill();
}
